import { memo, useState } from 'react'
import { X, ChevronDown, ChevronRight } from 'lucide-react'

interface Props {
  open: boolean
  onClose: () => void
}

interface Faq { q: string; a: string }

const FAQ: Faq[] = [
  {
    q: 'Что такое PolyStrike Visual?',
    a: 'Визуальный терминал для торговли на Polymarket: матрица ценовых диапазонов BTC / ETH / SOL × 3 дня поверх графика цены. Один тап по ячейке — лимитный ордер ДА / НЕТ через Polymarket CLOB. Long-press выделяет ячейки, сверху считаются суммы арбитража по колонке.',
  },
  {
    q: 'Как устроен кошелёк?',
    a: 'Non-custodial. Приватный ключ PM-кошелька (экспортируется с polymarket.com) шифруется PIN через PBKDF2-SHA256 (200 000 итераций) + AES-256-GCM и хранится только в localStorage этого браузера. Сервер ключ никогда не видит.',
  },
  {
    q: 'Как работает подписка?',
    a: 'Первые 7 дней после подключения PM-кошелька — trial с полным функционалом. Далее — 8.5 USDC / 30 дней. Оплата: вы сами подписываете on-chain transfer со своего PM-кошелька на treasury. Watcher видит транзакцию и активирует подписку. Ключ при этом не покидает браузер.',
  },
  {
    q: 'Что будет когда подписка истечёт?',
    a: 'Матрица и график остаются видимы (просмотр бесплатно), но размещение новых ордеров блокируется. Управлять открытыми позициями и делать redeem выигрышей можно на polymarket.com — логин через тот же PM-кошелёк.',
  },
  {
    q: 'Почему нужен PIN?',
    a: 'PIN — это пароль к AES-шифру вашего приватного ключа. Без PIN ключ не может быть расшифрован даже при физическом доступе к устройству. Рекомендуем 4+ цифр.',
  },
  {
    q: 'Арбитражная подсветка — что это?',
    a: 'Долгим тапом (1 с) вы выделяете ячейки. Сверху над колонкой показывается сумма цен ДА по выделенным. Если сумма < 100¢ — зелёный цвет, значит покупка всех этих диапазонов гарантированно выигрывает больше потраченного.',
  },
  {
    q: 'Auto-Redeem есть?',
    a: 'Нет — в Visual redeem ручной: кнопка активируется когда позиция резолвнулась в выигрыш. Gasless, полностью клиентский (через Polymarket Builder Relayer).',
  },
  {
    q: 'Что убрано по сравнению с полной версией?',
    a: 'Single-Cell Bot, Stop-Loss monitor, AI Trader, AutoBuy — всё это пока в разработке и вернётся после валидации спроса на базовом продукте.',
  },
]

function FaqItem({ item }: { item: Faq }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-lg border border-[#374151] bg-[#0d1117]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 px-3 py-2 text-left"
      >
        {open ? (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-[#94a3b8]" />
        ) : (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-[#94a3b8]" />
        )}
        <span className="text-sm text-white">{item.q}</span>
      </button>
      {open && <div className="px-3 pb-3 pl-9 text-xs leading-relaxed text-[#94a3b8]">{item.a}</div>}
    </div>
  )
}

export const ReadmeDialog = memo(function ReadmeDialog({ open, onClose }: Props) {
  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="readme-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Закрыть"
      />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-[#374151] bg-[#111827] p-5 shadow-xl max-h-[90dvh] overflow-y-auto">
        <div className="mb-4 flex items-center justify-between">
          <h2 id="readme-title" className="text-lg font-semibold text-white">О PolyStrike Visual</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[#94a3b8] hover:bg-white/10 hover:text-white"
            aria-label="Закрыть"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tier */}
        <section className="mb-5">
          <div className="rounded-lg border border-[#374151] bg-[#1f2937] px-3 py-3">
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-sm font-semibold text-[#22c55e]">PolyStrike Visual</span>
              <span className="text-xs text-[#94a3b8]">8.5 USDC / 30 дней</span>
            </div>
            <div className="mb-2 text-[11px] text-[#f59e0b]">Первые 7 дней — trial бесплатно</div>
            <ul className="space-y-1 text-xs text-[#e2e8f0]">
              <li className="flex items-start gap-1.5"><span className="mt-0.5 text-[#22c55e]">+</span><span>Матрица диапазонов BTC / ETH / SOL × 3 дня</span></li>
              <li className="flex items-start gap-1.5"><span className="mt-0.5 text-[#22c55e]">+</span><span>Live price + график 3 дня</span></li>
              <li className="flex items-start gap-1.5"><span className="mt-0.5 text-[#22c55e]">+</span><span>Лимитные BUY / SELL через Polymarket CLOB</span></li>
              <li className="flex items-start gap-1.5"><span className="mt-0.5 text-[#22c55e]">+</span><span>Gasless redeem выигрышей</span></li>
              <li className="flex items-start gap-1.5"><span className="mt-0.5 text-[#22c55e]">+</span><span>Non-custodial: ключ не покидает браузер</span></li>
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#94a3b8]">FAQ</h3>
          <div className="space-y-1.5">
            {FAQ.map((item, i) => <FaqItem key={i} item={item} />)}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="mb-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#94a3b8]">Риски</h3>
          <div className="rounded-lg border border-[#f59e0b]/30 bg-[#f59e0b]/10 px-3 py-2.5 text-[11px] leading-relaxed text-[#f59e0b]">
            PolyStrike Visual — интерфейс к Polymarket CLOB. Торговля прогнозами связана
            с риском полной потери средств. Non-custodial: приложение не контролирует
            ваши средства и не возмещает убытки. Вы несёте полную ответственность за
            свои решения. Проверьте доступность Polymarket в вашей юрисдикции.
          </div>
        </section>

        {/* Legal + Support */}
        <section className="mb-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#94a3b8]">Поддержка и документы</h3>
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center justify-between rounded-lg bg-[#1f2937] px-3 py-2">
              <span className="text-[#94a3b8]">Email</span>
              <a href="mailto:support@polystrike.app" className="text-[#22c55e] hover:text-[#16a34a]">
                support@polystrike.app
              </a>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-[#1f2937] px-3 py-2">
              <span className="text-[#94a3b8]">Telegram</span>
              <a
                href="https://t.me/PolyStrike_autobot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#22c55e] hover:text-[#16a34a]"
              >
                @PolyStrike_autobot
              </a>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-[#1f2937] px-3 py-2">
              <span className="text-[#94a3b8]">Условия использования</span>
              <a
                href="/tos"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#22c55e] hover:text-[#16a34a]"
              >
                polystrike.app/tos
              </a>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-[#1f2937] px-3 py-2">
              <span className="text-[#94a3b8]">Политика конфиденциальности</span>
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#22c55e] hover:text-[#16a34a]"
              >
                polystrike.app/privacy
              </a>
            </div>
          </div>
          <div className="mt-2 text-[10px] text-[#64748b]">
            SLA ответа поддержки — 24 часа в рабочие дни.
          </div>
        </section>

        <div className="mt-5 text-center text-[10px] text-[#64748b]">
          PolyStrike Visual · non-custodial · AES-256-GCM + PBKDF2-200k
        </div>
      </div>
    </div>
  )
})
